import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOrg } from '../lib/org';
import * as orgApi from '../api/organizations';
import {
  Spinner,
  ErrorAlert,
  EmptyState,
  StatusPill,
} from '../components/ui';
import { ui, cn } from '../lib/ui';

export default function OrgDirectory() {
  const { orgs: myOrgs, switchOrg } = useOrg();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [visFilter, setVisFilter] = useState('all');

  const myOrgIds = new Set(myOrgs.map(o => o.orgId));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await orgApi.listOrganizations({ status: 'active', limit: 100 });
      setOrgs(data.organizations || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orgs.filter(org => {
    if (visFilter === 'mine' && !myOrgIds.has(org.orgId)) return false;
    if (visFilter === 'public' && org.visibility !== 'public') return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (org.name || '').toLowerCase().includes(q) ||
      (org.slug || '').toLowerCase().includes(q) ||
      (org.description || '').toLowerCase().includes(q)
    );
  });

  if (loading) return <Spinner label="Loading organizations..." />;
  if (error) return <ErrorAlert error={error} />;

  return (
    <>
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className="m-0 text-title-l">Organizations</h1>
        <Link to="/orgs/new" className={ui.btnPrimary}>
          Create organization
        </Link>
      </div>

      <div className={cn(ui.row, 'mb-4')}>
        <input
          className={cn(ui.input, 'min-w-48 max-w-96 flex-1')}
          type="search"
          placeholder="Search organizations..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className={cn(ui.input, 'w-auto!')}
          value={visFilter}
          onChange={e => setVisFilter(e.target.value)}
        >
          <option value="all">All organizations</option>
          <option value="mine">My organizations</option>
          <option value="public">Public only</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState>{search || visFilter !== 'all' ? 'No matching organizations.' : 'No organizations available.'}</EmptyState>
      ) : (
        <div className={cn(ui.grid, ui.gridMd2)}>
          {filtered.map(org => (
            <OrgCard
              key={org.orgId}
              org={org}
              isMember={myOrgIds.has(org.orgId)}
              onSwitch={() => switchOrg(org.orgId)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function OrgCard({ org, isMember, onSwitch }) {
  return (
    <div className={cn(ui.card, 'p-4!')}>
      <div className={cn(ui.row, ui.spread, 'items-start')}>
        <div>
          <h3 className="mt-0 mb-1 text-title-s">{org.name}</h3>
          <p className={cn(ui.muted, 'mt-0 mb-2 text-body-s')}>
            {org.slug} &middot; {org.memberCount ?? 0} member{org.memberCount === 1 ? '' : 's'}
          </p>
        </div>
        <StatusPill status={org.tier} />
      </div>
      {org.description && (
        <p className="mt-0 mb-3 text-body-s">{org.description}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {isMember ? (
          <button className={ui.btnSecondary} onClick={onSwitch}>
            Switch to this org
          </button>
        ) : org.visibility === 'public' ? (
          <Link to={`/o/${org.slug}`} className={ui.btnSecondary}>
            View public page
          </Link>
        ) : (
          <span className={cn(ui.muted, 'text-body-s')}>Invite only</span>
        )}
      </div>
    </div>
  );
}
